import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface RegistrationStepperProps {
  steps: string[];
  activeStep: number;
  onStepClick?: (step: number) => void;
  visitedSteps: Set<number>;  // Track which steps have been visited
  stepErrors: Record<number, boolean>;  // Track which steps have validation errors
}

// Step status type
type StepStatus = 'active' | 'completed' | 'error' | 'unvisited';

const RegistrationStepper: React.FC<RegistrationStepperProps> = ({ 
  steps, 
  activeStep, 
  onStepClick, 
  visitedSteps, 
  stepErrors 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine step status
  const getStepStatus = (stepIndex: number): StepStatus => {
    if (stepIndex === activeStep) return 'active';
    if (!visitedSteps.has(stepIndex)) return 'unvisited';
    if (stepErrors[stepIndex]) return 'error';
    return 'completed';
  };

  // Get colors based on step status
  const getStepColors = (status: StepStatus) => {
    const isDark = theme.palette.mode === 'dark';
    switch (status) {
      case 'active':
        return {
          bg: isDark ? theme.palette.primary.dark : theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          border: isDark ? theme.palette.primary.light : theme.palette.primary.main,
          hoverBg: isDark ? theme.palette.primary.main : theme.palette.primary.dark,
        };
      case 'error':
        return {
          bg: isDark ? theme.palette.error.dark : theme.palette.error.light,
          color: isDark ? theme.palette.error.contrastText : theme.palette.error.dark,
          border: theme.palette.error.main,
          hoverBg: theme.palette.error.main,
        };
      case 'completed':
        return {
          bg: isDark ? theme.palette.success.dark : theme.palette.success.light,
          color: isDark ? theme.palette.success.contrastText : theme.palette.success.dark,
          border: theme.palette.success.main,
          hoverBg: theme.palette.success.main,
        };
      case 'unvisited':
      default:
        return {
          bg: isDark ? theme.palette.grey[800] : theme.palette.grey[100],
          color: theme.palette.text.secondary,
          border: isDark ? theme.palette.grey[600] : theme.palette.grey[300],
          hoverBg: isDark ? theme.palette.grey[700] : theme.palette.grey[200],
        };
    }
  };

  // Render a step button
  const renderStepButton = (label: string, idx: number) => {
    const status = getStepStatus(idx);
    const colors = getStepColors(status);
    const isActive = status === 'active';
    
    // On narrow screens, non-active steps show as circles with numbers only
    const showAsCircle = isNarrow && !isActive;
    
    // Circle size - smaller on narrow screens
    const circleSize = isNarrow ? 36 : 32;
    
    return (
      <Box
        key={idx}
        onClick={onStepClick ? () => onStepClick(idx) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: showAsCircle ? 0 : 2,
          py: 1,
          minHeight: circleSize + 8,
          minWidth: showAsCircle ? circleSize : 'auto',
          width: showAsCircle ? circleSize : 'auto',
          borderRadius: showAsCircle ? '50%' : 2,
          cursor: onStepClick ? 'pointer' : 'default',
          backgroundColor: colors.bg,
          color: colors.color,
          border: `2px solid`,
          borderColor: colors.border,
          transition: 'all 0.2s ease-in-out',
          flexShrink: showAsCircle ? 0 : 1,
          flexGrow: isActive && isNarrow ? 1 : 0,
          '&:hover': onStepClick ? {
            backgroundColor: colors.hoverBg,
            color: theme.palette.getContrastText(
              status === 'active' ? theme.palette.primary.dark :
              status === 'error' ? theme.palette.error.main :
              status === 'completed' ? theme.palette.success.main :
              theme.palette.grey[700]
            ),
            transform: 'scale(1.02)',
          } : {},
        }}
      >
        {/* Step number or checkmark */}
        <Box
          sx={{
            width: showAsCircle ? 'auto' : 24,
            height: showAsCircle ? 'auto' : 24,
            borderRadius: '50%',
            backgroundColor: showAsCircle ? 'transparent' : 
              isActive ? 'background.paper' : 'transparent',
            color: showAsCircle ? 'inherit' :
              isActive ? colors.border : 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: showAsCircle ? '0.9rem' : '0.8rem',
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {status === 'completed' ? '✓' : idx + 1}
        </Box>
        
        {/* Step label - hidden on narrow screens for non-active steps */}
        {!showAsCircle && (
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: isActive ? 700 : 500,
              whiteSpace: isNarrow ? 'normal' : 'nowrap',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
        py: 2,
        mb: 3,
        mx: -3, // Extend to edges of parent Paper
        px: 3,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* Step X of Y header */}
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          textAlign: 'center', 
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {t('common.stepOf', { current: activeStep + 1, total: steps.length })}
      </Typography>
      
      {/* Step buttons row */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        {steps.map((label, idx) => renderStepButton(label, idx))}
      </Box>
    </Box>
  );
};

export default RegistrationStepper;
